"use client";

import type { RefCallback } from "react";
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import generateId from "./generateId";

// Generic type for any class constructor
type StaticClassType<T = any> = new (element: HTMLElement, config?: any) => T;

// Define a generic interface for static class instances
interface StaticInstance {
  update?: (config: any) => void;
  destroy?: () => void;
}

/**
 * A hook to create and manage an instance of a "static" class
 * @param StaticClass The class to instantiate
 * @param config Configuration options for the class
 * @returns [ref callback, instance reference]
 */
const useStatic = <T extends StaticInstance>(
  StaticClass: StaticClassType<T>,
  config: Record<string, any> = {},
): [RefCallback<HTMLElement>, RefObject<T | null>] => {
  const instance = useRef<T | null>(null);
  const configRef = useRef(config);
  const elementRef = useRef<HTMLElement | null>(null);

  // Update config ref when config changes
  useEffect(() => {
    configRef.current = config ?? {};
  }, [config]);

  const ref = useCallback(
    (node: HTMLElement | null) => {
      // Clean up previous instance if element changes
      if (
        elementRef.current &&
        elementRef.current !== node &&
        instance.current
      ) {
        instance.current.destroy?.();
        instance.current = null;
      }

      elementRef.current = node;

      if (node) {
        // Only create new instance if we don't have one yet
        if (instance.current === null) {
          instance.current = new StaticClass(node, configRef.current);
        } else {
          // Update existing instance with new config
          instance.current.update?.(configRef.current);
        }
      }
    },
    [StaticClass],
  );

  // Cleanup on unmount or when StaticClass changes
  useEffect(() => {
    return () => {
      if (instance.current) {
        instance.current.destroy?.();
        instance.current = null;
      }
      elementRef.current = null;
    };
  }, [StaticClass]);

  return [ref, instance];
};

/**
 * A hook for creating React portals
 * From https://www.jayfreestone.com/writing/react-portals-with-hooks/
 * @param id The id of the target DOM element
 * @returns A DOM element that can be used as a portal container
 */
function usePortal(id: string): HTMLDivElement {
  const rootElemRef = useRef<HTMLDivElement | null>(null);

  useEffect(
    function setupElement() {
      if (typeof document === "undefined") return;

      // Create element if it doesn't exist
      if (!rootElemRef.current) {
        rootElemRef.current = document.createElement("div");
      }

      // Look for existing target dom element to append to
      const parentElem = document.querySelector(`#${id}`);

      // Add the detached element to the parent
      if (parentElem && rootElemRef.current) {
        parentElem.appendChild(rootElemRef.current);
      }

      // This function is run on unmount
      return function removeElement() {
        rootElemRef.current?.remove();
      };
    },
    [id],
  );

  function getRootElem(): HTMLDivElement {
    if (typeof document === "undefined") {
      throw new Error("usePortal must be used in a browser environment");
    }

    if (!rootElemRef.current) {
      rootElemRef.current = document.createElement("div");
    }
    return rootElemRef.current;
  }

  return getRootElem();
}

/**
 * Returns unique string for the `id` attribute.
 *
 * @param id Provided id
 * @param name Optional prefix
 * @returns A unique ID string
 */
const useUniqueId = (id?: string, name?: string): string =>
  useMemo(() => id ?? generateId(name), [id, name]);

/**
 * Hydration-safe hook for generating a unique, numeric ID for tooltips and similar components.
 * Ensures IDs are consistent between SSR and client, and match test expectations.
 * @param providedId Optional externally provided ID
 * @returns Hydration-safe unique ID string (e.g., "id-1")
 */
const idCounter = { current: 0 };

function useHydrationSafeId(providedId?: string): string {
  const [hydratedId, setHydratedId] = useState<string | undefined>(providedId);
  const idRef = useRef<string | undefined>(providedId);

  useEffect(() => {
    if (!providedId) {
      idCounter.current += 1;
      const newId = `id-${idCounter.current}`;
      idRef.current = newId;
      setHydratedId(newId);
    }
  }, [providedId]);

  return hydratedId || idRef.current || providedId || "id-0";
}

export { useHydrationSafeId, usePortal, useStatic, useUniqueId };
